import { createSignal, onMount, createEffect, Show, onCleanup } from "solid-js";
import { Portal } from "solid-js/web";

import { getData } from "../../utils/global";
import { persistantStore } from "../../store/persistantStore";
import { themeStore } from "../../store/themeStore";
import { apiEndpoints } from "@config/env";
import { error as logError, debug as logDebug } from "../../utils/console";

import "../../styles/metadata-picker.css";

const { selectedProjectId, selectedClassName } = persistantStore;

const METADATA_PICKER_PREVIEW_COUNT = 3;
const METADATA_PICKER_SEARCH_DISPLAY_LIMIT = 200;

export type MetadataPickerType = "event" | "config" | "year" | "crew";

export interface MetadataPickerProps {
  metadataType: MetadataPickerType;
  onSave: (values: string[]) => void;
  onCancel?: () => void;
  /** Pre-selected values (e.g. from comma-separated filter string) */
  initialSelected?: string[];
  /** Lowercased source names — same contract as /maneuvers-history */
  getSourceNames: () => string[];
  /** Optional date scope (datasets.date); omit keys when not set */
  getDateRange?: () => { startDate: string; endDate: string };
  isUpdate?: boolean;
}

interface SearchItem {
  name: string;
}

function normalizeInitial(raw: string[] | undefined): string[] {
  if (!raw || !Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const s of raw) {
    const t = String(s).trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

function titleForType(t: MetadataPickerType): string {
  if (t === "year") return "Select Years";
  if (t === "config") return "Select Configs";
  if (t === "crew") return "Select Crews";
  return "Select Events";
}

function searchPlaceholder(t: MetadataPickerType): string {
  if (t === "year") return "Search years…";
  if (t === "config") return "Search configs…";
  if (t === "crew") return "Search crews…";
  return "Search events…";
}

const MetadataPicker = (props: MetadataPickerProps) => {
  const { onSave, onCancel, metadataType, initialSelected = [], getSourceNames, getDateRange, isUpdate = false } = props;

  const [search, setSearch] = createSignal("");
  const [searchItems, setSearchItems] = createSignal<SearchItem[]>([]);
  const [searchMatches, setSearchMatches] = createSignal<string[]>([]);
  const [availableValues, setAvailableValues] = createSignal<string[]>([]);
  const [selectedValues, setSelectedValues] = createSignal<string[]>([]);
  const [isModalOpen, setModalOpen] = createSignal(true);
  const [loadError, setLoadError] = createSignal<string | null>(null);
  const [loading, setLoading] = createSignal(true);
  let searchInputRef: HTMLInputElement | undefined;
  let fetchController: AbortController | null = null;

  const mergeInitialWithFetched = (fetched: string[], initial: string[]): { selected: string[]; available: string[] } => {
    const fetchedLower = new Map<string, string>();
    fetched.forEach((v) => {
      const key = v.toLowerCase();
      if (!fetchedLower.has(key)) fetchedLower.set(key, v);
    });
    const selected: string[] = [];
    const seenSel = new Set<string>();
    for (const raw of initial) {
      const t = String(raw).trim();
      if (!t) continue;
      const canon = fetchedLower.get(t.toLowerCase()) ?? t;
      const k = canon.toLowerCase();
      if (seenSel.has(k)) continue;
      seenSel.add(k);
      selected.push(canon);
    }
    const selectedLower = new Set(selected.map((s) => s.toLowerCase()));
    const available = fetched.filter((v) => !selectedLower.has(v.toLowerCase()));
    return { selected, available };
  };

  const fetchMetadata = async (): Promise<void> => {
    fetchController?.abort();
    fetchController = new AbortController();
    setLoading(true);
    setLoadError(null);

    const className = selectedClassName();
    const projectId = selectedProjectId();
    const names = getSourceNames().map((n) => String(n).trim().toLowerCase()).filter((n) => n.length > 0);

    if (!className || !projectId) {
      setLoadError("Missing class or project.");
      setLoading(false);
      setAvailableValues([]);
      return;
    }
    if (names.length === 0) {
      setLoadError("No sources selected — choose data sources first.");
      setLoading(false);
      setAvailableValues([]);
      return;
    }

    let url = `${apiEndpoints.app.data}/metadata-values?type=${encodeURIComponent(metadataType)}&class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(String(projectId))}&source_names=${encodeURIComponent(JSON.stringify(names))}`;

    const dr = getDateRange?.();
    if (dr?.startDate?.trim() && dr?.endDate?.trim()) {
      url += `&start_date=${encodeURIComponent(dr.startDate.trim())}&end_date=${encodeURIComponent(dr.endDate.trim())}`;
    }

    try {
      const response = await getData(url, fetchController.signal);
      if (response?.type === "AbortError" || response?.error === "Request cancelled") {
        return;
      }
      if (response.success && Array.isArray(response.data)) {
        const fetched: string[] = response.data.map((x: unknown) => String(x).trim()).filter((s: string) => s.length > 0);
        const initial = normalizeInitial(initialSelected);
        const { selected, available } = mergeInitialWithFetched(fetched, initial);
        setSelectedValues(selected);
        setAvailableValues(available);
        logDebug("MetadataPicker: loaded", { type: metadataType, count: fetched.length, selected: selected.length });
      } else {
        setLoadError(typeof response?.message === "string" ? response.message : "Failed to load values");
        setAvailableValues([]);
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === "AbortError") {
        return;
      }
      logError("MetadataPicker: fetch error", err);
      setLoadError(err instanceof Error ? err.message : "Failed to load values");
      setAvailableValues([]);
    } finally {
      setLoading(false);
      fetchController = null;
    }
  };

  onMount(async () => {
    await fetchMetadata();
    setTimeout(() => {
      searchInputRef?.focus();
    }, 100);
  });

  onCleanup(() => {
    fetchController?.abort();
    fetchController = null;
  });

  createEffect(() => {
    if (isModalOpen() && searchInputRef) {
      setTimeout(() => {
        searchInputRef?.focus();
      }, 100);
    }
  });

  const fillSearch = (): void => {
    const searchText = search().toLowerCase().trim();
    const pool = availableValues();
    if (searchText.length > 0) {
      const tokens = searchText.includes(" ") ? searchText.split(/\s+/) : [searchText];
      const filtered = pool.filter((v) => {
        const lower = v.toLowerCase();
        return tokens.every((t) => lower.includes(t));
      });
      setSearchMatches(filtered);
      setSearchItems(filtered.slice(0, METADATA_PICKER_SEARCH_DISPLAY_LIMIT).map((name) => ({ name })));
    } else {
      setSearchMatches([]);
      setSearchItems([]);
    }
  };

  const selectAllSearchMatches = (): void => {
    const matches = searchMatches();
    if (matches.length === 0) return;
    const selectedLower = new Set(selectedValues().map((v) => v.toLowerCase()));
    const toAdd = matches.filter((name) => !selectedLower.has(name.toLowerCase()));
    if (toAdd.length === 0) return;
    setSelectedValues((prev) => [...prev, ...toAdd]);
    const toAddLower = new Set(toAdd.map((v) => v.toLowerCase()));
    setAvailableValues((prev) => prev.filter((v) => !toAddLower.has(v.toLowerCase())));
    setSearchItems((prev) => prev.filter((it) => !toAddLower.has(it.name.toLowerCase())));
    setSearchMatches((prev) => prev.filter((v) => !toAddLower.has(v.toLowerCase())));
  };

  const handleSelection = (value: string, isChecked: boolean): void => {
    if (isChecked) {
      setSelectedValues((prev) => [...prev, value]);
      setAvailableValues((prev) => prev.filter((v) => v !== value));
      setSearchItems((prev) => prev.filter((it) => it.name !== value));
    } else {
      setSelectedValues((prev) => prev.filter((v) => v !== value));
      setAvailableValues((prev) => [...prev, value].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })));
      setSearchItems((prev) => [...prev, { name: value }]);
    }
  };

  const save = (list: string[]): void => {
    setModalOpen(false);
    let values = list;
    // Config picker: empty selection + active search → use search as contains-match token (e.g. "M21")
    if (metadataType === "config" && values.length === 0 && search().trim()) {
      values = [search().trim()];
    }
    onSave(values);
  };

  const cancel = (): void => {
    setModalOpen(false);
    if (onCancel) {
      onCancel();
    }
  };

  const previewItems = (): string[] =>
    availableValues().slice(0, METADATA_PICKER_PREVIEW_COUNT);

  const showPreview = (): boolean =>
    !loading() && !loadError() && search().length === 0 && previewItems().length > 0;

  return (
    <Portal>
      <div class={`modal ${isModalOpen() ? "block" : "hidden"} ${themeStore.isDark() ? "dark" : "light"}`}>
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{titleForType(metadataType)}</h5>
              <button type="button" class="close" onClick={() => cancel()}>
                &times;
              </button>
            </div>
            <div class="modal-body">
              <Show when={loadError()}>
                <p class="metadata-picker-muted mb-3">{loadError()}</p>
              </Show>
              <Show when={loading()}>
                <p class="metadata-picker-muted mb-3">Loading…</p>
              </Show>
              <div class="mb-4">
                <label for="metadata-picker-search" class="metadata-picker-label">
                  Search
                </label>
                <input
                  ref={searchInputRef}
                  id="metadata-picker-search"
                  type="text"
                  class="metadata-picker-search-input"
                  value={search()}
                  onInput={(e) => {
                    setSearch((e.target as HTMLInputElement).value);
                    fillSearch();
                  }}
                  placeholder={searchPlaceholder(metadataType)}
                  autocomplete="off"
                />
              </div>
              <Show when={showPreview()}>
                <div class="metadata-picker-panel" id="metadata-picker-preview">
                  <div class="metadata-picker-preview-header">
                    <div class="text-sm font-medium metadata-picker-muted">Available</div>
                    <p class="metadata-picker-preview-hint">Select below or search for more</p>
                  </div>
                  {previewItems().map((name, idx) => {
                    const inputId = `md-preview-${metadataType}-${idx}-${name.length}`;
                    return (
                      <div class="metadata-picker-row">
                        <input
                          type="checkbox"
                          id={inputId}
                          checked={false}
                          onChange={(e) => {
                            handleSelection(name, (e.target as HTMLInputElement).checked);
                          }}
                        />
                        <label for={inputId}>
                          {name}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </Show>
              <Show when={search().length > 0}>
                <div class="metadata-picker-panel" id="metadata-picker-search-results">
                  <Show when={metadataType === "config" && searchMatches().length > 0}>
                    <div class="metadata-picker-search-actions">
                      <span class="metadata-picker-muted">
                        {searchMatches().length} match{searchMatches().length === 1 ? "" : "es"}
                      </span>
                      <button
                        type="button"
                        class="metadata-picker-link-btn"
                        onClick={() => selectAllSearchMatches()}
                      >
                        Select all matches
                      </button>
                      <button
                        type="button"
                        class="metadata-picker-link-btn"
                        onClick={() => save([])}
                      >
                        Use &quot;{search().trim()}&quot; as filter
                      </button>
                    </div>
                  </Show>
                  {searchItems().length > 0 ? (
                    searchItems().map((item, idx) => {
                      const inputId = `md-pick-${metadataType}-${idx}-${item.name.length}`;
                      return (
                      <div class="metadata-picker-row">
                        <input
                          type="checkbox"
                          id={inputId}
                          checked={selectedValues().some((s) => s === item.name)}
                          onChange={(e) => {
                            handleSelection(item.name, (e.target as HTMLInputElement).checked);
                          }}
                        />
                        <label for={inputId}>
                          {item.name}
                        </label>
                      </div>
                      );
                    })
                  ) : (
                    <p class="metadata-picker-muted">No matches.</p>
                  )}
                </div>
              </Show>
              <Show when={selectedValues().length > 0}>
                <div class="metadata-picker-panel" id="metadata-picker-selected">
                  <div class="text-sm font-medium mb-2 metadata-picker-muted">Selected</div>
                  {selectedValues().map((val) => (
                    <div class="metadata-picker-row">
                      <input type="checkbox" checked onChange={() => handleSelection(val, false)} />
                      <span>{val}</span>
                    </div>
                  ))}
                </div>
              </Show>
            </div>
            <div class="modal-footer">
              <button type="button" class="metadata-picker-save-btn" onClick={() => save(selectedValues())}>
                {isUpdate ? "Update" : "Apply"}
              </button>
              <button type="button" class="btn btn-secondary metadata-picker-secondary-btn" onClick={() => cancel()}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default MetadataPicker;
